// ── Live zone occupancy ───────────────────────────────────────────────────
// One fleet-wide DeviceStatusInfo call gives every vehicle's last reported
// position and current driver; each is tested against the zone grid. This layer
// is independent of the historical analysis — it works the moment zones load,
// and keeps working while an analysis runs.
//
// Only ACTIVE devices are shown. DeviceStatusInfo returns archived ones too,
// still carrying the last position they ever reported, so an unfiltered read
// slowly fills every yard with units retired years ago. A parked active
// vehicle IS legitimately "inside" (that's the yard-management case), so
// staleness is badged rather than hidden — but a decommissioned asset is not
// a vehicle in the zone at all.

import { callGeotabAPI } from '../api/geotab-client';
import { state } from '../state';
import type { DeviceStatusInfo } from '../api/types';
import { pointInZone, zonesAtPoint } from './zone-activity-geometry';
import { projectToPlate } from './zone-activity-plate';
import type { LiveOccupancy, LiveOccupant } from './zone-activity-types';
import { loadZoneActivityPageData } from './zone-activity-data';

/** A position older than this is still shown — a parked vehicle's last fix IS
 *  its position — but badged, so nobody mistakes it for a live report. */
export const STALE_MS = 60 * 60 * 1000;

let _rawDsi: DeviceStatusInfo[] = [];
let _occupancy: LiveOccupancy | null = null;
let _scope: Set<string> | null = null;      // null = every device
let _onUpdate: (() => void) | null = null;

let _refreshTimer: ReturnType<typeof setInterval> | null = null;
let _inFlight = false;

export function getLiveOccupancy(): LiveOccupancy | null {
  return _occupancy;
}

export function setOnLiveUpdate(cb: (() => void) | null): void {
  _onUpdate = cb;
}

/** Restrict the live layer to a set of device ids (the page's group filter). */
export function setLiveScope(deviceIds: Set<string> | null): void {
  _scope = deviceIds;
  if (_rawDsi.length) {
    void computeFromCache();
  }
}

/** Refresh cadence scales with fleet size, matching the Vehicle Health loop. */
function refreshIntervalMs(deviceCount: number): number {
  if (deviceCount > 2000) return 60_000;
  if (deviceCount > 500)  return 45_000;
  return 30_000;
}

async function computeFromCache(): Promise<void> {
  const data = await loadZoneActivityPageData();
  const now  = Date.now();

  const byZone = new Map<string, LiveOccupant[]>();
  let positioned = 0;

  for (const dsi of _rawDsi) {
    const deviceId = dsi?.device?.id;
    if (!deviceId) continue;
    // DeviceStatusInfo is fleet-wide and includes ARCHIVED/decommissioned
    // devices, which keep reporting their last known position forever — a
    // yard would otherwise fill with units retired years ago (and render
    // under their raw Geotab id, since an archived device is absent from the
    // name map). `deviceNames` is the already-filtered active,
    // telematics-equipped asset list from zone-activity-data.ts, and is the
    // authority on what counts as a real vehicle here.
    const deviceName = data.deviceNames.get(deviceId);
    if (deviceName === undefined) continue;
    if (_scope && !_scope.has(deviceId)) continue;

    const lat = dsi.latitude;
    const lng = dsi.longitude;
    if (typeof lat !== 'number' || typeof lng !== 'number') continue;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    // 0,0 is Geotab's "no fix yet" sentinel, not a position in the Atlantic.
    if (lat === 0 && lng === 0) continue;
    positioned++;

    const candidates = zonesAtPoint(data.grid, lat, lng);
    if (!candidates.length) continue;

    const lastCommMs = dsi.dateTime ? new Date(dsi.dateTime).getTime() : 0;
    const driverId   = dsi.driver?.id;
    const driverName =
      driverId && driverId !== 'UnknownDriverId'
        ? (data.userMap.get(driverId) ?? null)
        : null;

    for (const idx of candidates) {
      const zone = data.grid.zones[idx];
      if (!pointInZone(lat, lng, zone)) continue;

      const pt = projectToPlate(zone.plate.tx, lat, lng);
      const occupant: LiveOccupant = {
        deviceId,
        deviceName,
        driverId: driverId && driverId !== 'UnknownDriverId' ? driverId : null,
        driverName,
        isDriving: dsi.isDriving === true,
        lastCommMs,
        isCommunicating: dsi.isDeviceCommunicating !== false,
        isStale: lastCommMs > 0 && now - lastCommMs > STALE_MS,
        plateX: pt ? pt.x : 50,
        plateY: pt ? pt.y : 50,
      };

      const bucket = byZone.get(zone.id);
      if (bucket) bucket.push(occupant);
      else byZone.set(zone.id, [occupant]);
    }
  }

  // Driving first, then stale last, then by name — the roster shows the most
  // operationally interesting vehicles before it truncates to "+N".
  byZone.forEach(list => list.sort((a, b) => {
    if (a.isStale !== b.isStale) return a.isStale ? 1 : -1;
    if (a.isDriving !== b.isDriving) return a.isDriving ? -1 : 1;
    return a.deviceName.localeCompare(b.deviceName);
  }));

  _occupancy = { byZone, updatedAtMs: now, positionedDeviceCount: positioned };
  _onUpdate?.();
}

/**
 * Pull current positions and recompute occupancy. Overlapping calls are
 * skipped, and a response that lands after a logout is discarded.
 */
export async function refreshLiveOccupancy(): Promise<void> {
  if (_inFlight) return;
  _inFlight = true;
  const epoch = state.sessionEpoch;
  try {
    const rows = await callGeotabAPI<DeviceStatusInfo[]>('Get', { typeName: 'DeviceStatusInfo' });
    if (epoch !== state.sessionEpoch) return;
    _rawDsi = rows ?? [];
    await computeFromCache();
  } finally {
    if (epoch === state.sessionEpoch) _inFlight = false;
  }
}

export function startZoneActivityRefresh(): void {
  if (_refreshTimer) return;                    // already running — don't stack
  const count = _rawDsi.length || state.allVehicles.length;
  _refreshTimer = setInterval(() => {
    void refreshLiveOccupancy().catch(() => { /* transient; next tick retries */ });
  }, refreshIntervalMs(count));
}

export function stopZoneActivityRefresh(): void {
  if (_refreshTimer) {
    clearInterval(_refreshTimer);
    _refreshTimer = null;
  }
}

export function resetZoneActivityLive(): void {
  stopZoneActivityRefresh();
  _rawDsi    = [];
  _occupancy = null;
  _scope     = null;
  _onUpdate  = null;
  _inFlight  = false;
}
